import React, { useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import PropTypes from "prop-types";
import { Box, Checkbox, Typography } from "@mui/material";
import SelectField from "Components/Common/SelectField";
import DateField from "Components/Common/DatePicker";
import TimeField from "Components/Common/Timepicker";
import { getOptionValue } from "common/enums";
import {
  useAccessDetailBlockQuery,
  useLazyAccessDetailDoorsQuery,
  useLazyAccessDetailLocationsQuery,
} from "apis/UtilityTunnel/AccessCardDetails";
import moment from "moment";

const AccessDetails = ({ formik, t }) => {
  const { data: { data: blocks = [] } = {} } = useAccessDetailBlockQuery();
  const [getLocations, { data: { data: locations = [] } = {} }] =
    useLazyAccessDetailLocationsQuery();
  const [getDoors, { data: { data: doors = [] } = {} }] =
    useLazyAccessDetailDoorsQuery();

  const {
    values: {
      extraValues: { blockId },
      accessDetails: {
        locationId,
        accessCardHolderType,
        isAcknowledgment,
        expectedReturnDate,
        accessRequiredFrom,
        accessRequiredTo,
      },
      employeeContractorDetail: { workingHoursFrom, workingHoursTo },
    },
    errors,
    touched,
    setFieldValue,
  } = formik;

  const isEmployee =
    getOptionValue("utilityTunnelConsumerType", "Employee") ===
    accessCardHolderType;

  useEffect(() => {
    if (blockId) {
      getLocations({ blockId });
    }
  }, [blockId, getLocations]);

  useEffect(() => {
    if (locationId) {
      getDoors({ locationId });
    }
  }, [locationId, getDoors]);

  return (
    <>
      <Row>
        <Col md={4} sm={6}>
          <SelectField
            label={t("Block") + "*"}
            name="extraValues.blockId"
            formik={formik}
            options={blocks}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            onChange={(_, option) => {
              setFieldValue("extraValues.blockId", option.value);
              setFieldValue("accessDetails.locationId", "");
              setFieldValue("accessDetails.doorId", "");
            }}
          />
        </Col>
        <Col md={4} sm={6}>
          <SelectField
            label={t("Location") + "*"}
            name="accessDetails.locationId"
            formik={formik}
            options={locations}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            onChange={(_, option) => {
              setFieldValue("accessDetails.locationId", option.value);
              setFieldValue("accessDetails.doorId", "");
            }}
            disabled={!blockId}
          />
        </Col>
        <Col md={4} sm={6}>
          <SelectField
            label={t("Door") + "*"}
            name="accessDetails.doorId"
            formik={formik}
            options={doors}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            disabled={!(blockId && locationId)}
          />
        </Col>

        <Col md={4} sm={6}>
          <DateField
            label={t("Access Required From") + "*"}
            name="accessDetails.accessRequiredFrom"
            minDate={moment()}
            maxDate={accessRequiredTo}
            formik={formik}
            format="DD-MM-YYYY"
          />
        </Col>

        <Col md={4} sm={6}>
          <DateField
            label={t("Access Required To") + "*"}
            name="accessDetails.accessRequiredTo"
            minDate={accessRequiredFrom}
            formik={formik}
            format="DD-MM-YYYY"
          />
        </Col>

        {!isEmployee && (
          <>
            <Col md={4} sm={6}>
              <DateField
                label={t("Expected Return Date")}
                name="accessDetails.expectedReturnDate"
                formik={formik}
                minDate={accessRequiredTo}
                format="DD-MM-YYYY"
              />
            </Col>
            <Col md={4} sm={6}>
              <TimeField
                label={t("Working Hours From")}
                name="employeeContractorDetail.workingHoursFrom"
                formik={formik}
                onChange={(newValue) => {
                  setFieldValue(
                    "employeeContractorDetail.workingHoursFrom",
                    newValue
                  );
                }}
              />
            </Col>
            <Col md={4} sm={6}>
              <TimeField
                label={t("Working Hours To")}
                name="employeeContractorDetail.workingHoursTo"
                formik={formik}
                onChange={(newValue) => {
                  setFieldValue(
                    "employeeContractorDetail.workingHoursTo",
                    newValue
                  );
                }}
              />
            </Col>
          </>
        )}

        <Col md={12} sm={6}>
          <Box sx={{ display: "flex", alignItems: "start" }}>
            <Checkbox
              size="small"
              name="accessDetails.isAcknowledgment"
              checked={isAcknowledgment}
              onChange={(e) =>
                setFieldValue(
                  "accessDetails.isAcknowledgment",
                  e.target.checked
                )
              }
            />
            <Box mt={1}>
              {isEmployee ? (
                <div>
                  <strong>Employee Acknowledgment:</strong>
                  <p>
                    I acknowledge receipt of the access card and agree to use it
                    responsibly and return it upon termination of employment or
                    as requested by the company.
                  </p>
                </div>
              ) : (
                <div>
                  <strong>Contractor Declaration:</strong>
                  <ul>
                    <li>
                      Use it only for the purpose for which it was issued.
                    </li>
                    <li>Not allow any other person to use the card.</li>
                    <li>
                      Return the card on or before the expected return date.
                    </li>
                    <li>Report loss or damage immediately.</li>
                    <li>
                      If any unauthorized use is found, legal action will be
                      taken as per GIFT Policy.
                    </li>
                  </ul>
                </div>
              )}
            </Box>
          </Box>
          {!!touched?.accessDetails?.isAcknowledgment &&
            !!errors?.accessDetails?.isAcknowledgment && (
              <>
                <Typography color="error">
                  {errors?.accessDetails.isAcknowledgment}
                </Typography>
              </>
            )}
        </Col>
      </Row>
    </>
  );
};

AccessDetails.propTypes = {
  formik: PropTypes.object,
  isEditable: PropTypes.bool,
  t: PropTypes.func,
};
export default withTranslation()(AccessDetails);
